import { type PropsWithChildren, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, NavLink, useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { type RootState, UserRole } from '../../common/types';
import { ReactComponent as AnalyticsIcon } from '../../dashboard/assets/icons/AnalyticsIcon.svg';
import { ReactComponent as DashboardIcon } from '../../dashboard/assets/icons/DashboardIcon.svg';
import { ReactComponent as EarningsIcon } from '../../dashboard/assets/icons/EarningsIcon.svg';
import { ReactComponent as LogOutIcon } from '../../dashboard/assets/icons/LogOutIcon.svg';
import { ReactComponent as VideoIcon } from '../../dashboard/assets/icons/VideoIcon.svg';
import { logout } from '../../store/auth';
import { ReactComponent as UploadIcon } from '../../upload/assets/icons/upload.svg';
import { ReactComponent as UploadsIcon } from '../../upload/assets/icons/uploads.svg';
import styles from './PrivateLayout.module.scss';

const defaultSideMenu = [
  {
    link: '/dashboard',
    iconComponent: <DashboardIcon />,
    title: 'Dashboard',
    showMobile: false,
  },
  {
    link: '/dashboard/myVideos',
    iconComponent: <VideoIcon />,
    title: 'My Videos',
    showMobile: false,
  },
  {
    link: '/dashboard/analytics',
    iconComponent: <AnalyticsIcon />,
    title: 'Channel Analytics',
    showMobile: false,
  },
  {
    link: '/dashboard/earnings',
    iconComponent: <EarningsIcon />,
    title: 'Earnings',
    showMobile: false,
  },
  {
    link: '/upload',
    iconComponent: <UploadIcon />,
    title: 'Upload Content',
    showMobile: false,
  },
  {
    link: '/uploads',
    iconComponent: <UploadsIcon />,
    title: 'Your Uploads',
    showMobile: true,
  },
];

const shortSideMenu = [
  {
    link: '/dashboard',
    iconComponent: <DashboardIcon />,
    title: 'Dashboard',
    showMobile: false,
  },
  {
    link: '/dashboard/myVideos',
    iconComponent: <VideoIcon />,
    title: 'My Videos',
    showMobile: false,
  },
  {
    link: '/dashboard/analytics',
    iconComponent: <AnalyticsIcon />,
    title: 'Channel Analytics',
    showMobile: false,
  },
  {
    link: '/dashboard/earnings',
    iconComponent: <EarningsIcon />,
    title: 'Earnings',
    showMobile: false,
  },
];

const PrivateLayout: React.FC<PropsWithChildren> = ({ children }) => {
  const history = useHistory();

  const { user } = useSelector((state: RootState) => state.auth);
  const { selectedCreatorProfile } = useSelector((state: RootState) => state.global);

  const creatorNickName =
    selectedCreatorProfile?.name?.length > 12
      ? `${selectedCreatorProfile?.name?.slice(0, 12)}...`
      : selectedCreatorProfile?.name || '';

  const isUserCreator = user?.role === UserRole.Creator;
  const sideMenu = isUserCreator ? defaultSideMenu : shortSideMenu;

  const { pathname } = history.location;

  const [selectedItem, setSelectedItem] = useState(pathname);

  const dispatch = useDispatch();

  useEffect(() => {
    setSelectedItem(pathname.startsWith('/dashboard/myVideos') ? '/dashboard/myVideos' : pathname);
  }, [pathname]);

  const handleLogout = () => {
    dispatch(logout(history) as any);
  };

  const handleGoBack = () => {
    history.push('/creators');
  };

  return (
    <main className={styles.layout}>
      <aside className={styles.sideBarContainer}>
        <div>
          {/* @ts-ignore - React Router v5 types are incompatible with React 18 */}
          <Link className={styles.logo} to="/">
            <img alt="logo" src="/assets/images/vuz_creators_logo_vertical.png" />
          </Link>
          {sideMenu.map((sideMenuElement) => {
            const menuLinkClassName = classNames(styles.menuItem, {
              [styles.selectedMenuItem]: selectedItem === sideMenuElement.link,
            });

            return (
              // @ts-ignore - React Router v5 types are incompatible with React 18 */
              <NavLink key={sideMenuElement.link} to={sideMenuElement.link}>
                <div className={menuLinkClassName}>
                  <div className={styles.menuItemIconContainer}>
                    {sideMenuElement.iconComponent}
                  </div>
                  <p>{sideMenuElement.title}</p>
                </div>
              </NavLink>
            );
          })}
        </div>
        <div className={styles.bottomSidebarContainer}>
          {selectedCreatorProfile && !isUserCreator && (
            <div className={styles.selectedCreatorContainer}>
              <div className={styles.view}>
                <img alt="view eye" src="/assets/icons/ViewEyeIcon.svg" />
                <p className={styles.viewText}>You are currently Viewing:</p>
              </div>
              <div className={styles.profile}>
                <img
                  alt="avatar"
                  className={styles.profileAvatar}
                  src={selectedCreatorProfile?.image || '/assets/images/Avatar.png'}
                />
                <p className={styles.profileText}>{creatorNickName}</p>
              </div>
              <button className={styles.switchButton} onClick={handleGoBack}>
                <img alt="switch profile" src="/assets/icons/SwitchProfileIcon.svg" />
                Switch Profile
              </button>
            </div>
          )}
          <div className={styles.logOutContainer} onClick={handleLogout}>
            <LogOutIcon />
            <p>Log Out</p>
          </div>
        </div>
      </aside>
      <div className={styles.mainSection}>{children}</div>
    </main>
  );
};

export default PrivateLayout;
